<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Buttons from '@/components/ui/Buttons.vue';
import ApplicationsList from '@/components/ApplicationsList.vue';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const checkIsBot = () => {
  if (process.client) return false;
  const headers = useRequestHeaders(['user-agent']);
  const ua = headers['user-agent'] || '';
  return /bot|googlebot|crawler|spider|robot|crawling|google web preview|chrome-lighthouse/i.test(ua);
};

const animationStep = ref(checkIsBot() ? 6 : 0);
const timers = ref([]);

const setTimer = (callback, delay) => {
  const id = window.setTimeout(callback, delay);
  timers.value.push(id);
};

onMounted(async () => {
  animationStep.value = 0;
  await nextTick();

  animationStep.value = 1;

  setTimer(() => {
    animationStep.value = 2;
  }, 3100);

  setTimer(() => {
    animationStep.value = 3;
  }, 3500);

  setTimer(() => {
    animationStep.value = 4;
  }, 3700);

  setTimer(() => {
    animationStep.value = 5;
  }, 4000);

  setTimer(() => {
    animationStep.value = 6;
  }, 4500);

  setTimer(() => {
    animationStep.value = 7;
  }, 4900);

  setTimer(() => {
    animationStep.value = 6;
  }, 7200);
});

onBeforeUnmount(() => {
  timers.value.forEach((id) => window.clearTimeout(id));
});
if (process.server) {
  useHead({
    noscript: [
      {
        innerHTML: `
          <style>
            .hero-main__media,
            .hero-main__media--active,
            .hero-main__image,
            .hero-main__title,
            .hero-main__buttons,
            .hero-main__services-item,
            .hero-main__applications { opacity: 1 !important; transform: none !important; }
          </style>
        `,
      },
    ],
  });
}
</script>
<template>
  <section :id="data.id" class="section section--hero bg-gray">
    <div class="container">
      <div id="hero-trigger" class="hero-main">
        <div class="hero-main__top">
          <div
            class="hero-main__media"
            :class="{ 'hero-main__media--active': animationStep >= 1 }"
          >
            <div class="hero-main__media-wrap">
              <svg
                width="938"
                height="697"
                viewBox="0 0 938 697"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="hero-main__svg"
                :class="{ 'hero-main__svg--active': animationStep >= 1 }"
              >
                <g clip-path="url(#clip0_5915_122071)">
                  <path
                    d="M581.776 428.292C562.309 455.33 547.479 469.845 463.891 462.36C391.43 455.871 219.469 406.121 190.809 354.209M127 489.939C134.03 320.682 161.187 259.52 169.72 246.598C188.646 217.938 209.736 203.878 228.121 185.493C236.79 176.824 243.803 163.322 277.33 137.365C288.506 129.615 295.608 124.82 321.132 109.246"
                    stroke="url(#paint0_linear_5915_122071)"
                    class="hero-main__svg-elem-1"
                  />
                  <circle
                    cx="305.449"
                    cy="338.529"
                    r="49.2496"
                    stroke="black"
                    class="hero-main__svg-elem-2"
                  />
                  <circle
                    cx="457.943"
                    cy="387.197"
                    r="37.353"
                    stroke="black"
                    class="hero-main__svg-elem-3"
                  />
                  <circle
                    cx="305.45"
                    cy="338.526"
                    r="75.2059"
                    stroke="black"
                    class="hero-main__svg-elem-4"
                  />
                  <circle
                    cx="457.942"
                    cy="387.197"
                    r="57.9017"
                    stroke="black"
                    class="hero-main__svg-elem-5"
                  />
                  <path
                    d="M552.575 278.062C560.856 281.125 569.089 284.155 577.45 287.155C580.154 272.375 590.645 240.651 610.977 231.998C655.319 213.128 825.117 205.098 982.477 198.27M552.575 278.062C490.696 255.169 426.137 230.395 285.441 202.256C284.12 202.016 282.672 201.764 281.116 201.514M552.575 278.062C541.76 313.31 594.214 414.233 601.784 426.671C609.355 439.108 621.792 463.442 610.977 473.176C609.154 474.816 605.966 476.257 601.784 477.519M281.116 201.514C261.651 198.392 225.338 195.646 210.817 221.183C210.112 222.422 209.386 223.683 208.643 224.969M281.116 201.514C281.656 168.73 275.708 135.744 330.324 103.298C374.018 77.342 405.129 45.6176 415.223 33M208.643 224.969C192.859 252.301 168.158 281.68 192.432 360.698C203.063 395.307 313.02 439.649 336.273 446.138C389.807 465.064 462.81 485.613 512.019 485.613C536.364 485.613 581.158 483.744 601.784 477.519M208.643 224.969C207.205 224.969 215.202 222.582 255.159 231.998C424.957 272.014 500.802 308.892 512.019 327.171C570.42 422.345 617.466 456.953 601.784 477.519"
                    stroke="url(#paint1_linear_5915_122071)"
                    class="hero-main__svg-elem-6"
                  />
                  <path
                    d="M183.239 315.273C185.402 386.653 175.669 411.528 266.516 454.248C357.363 496.968 522.834 521.842 558.524 526.168"
                    stroke="url(#paint2_linear_5915_122071)"
                    class="hero-main__svg-elem-7"
                  />
                  <path
                    d="M980.855 284.451C816.465 288.236 664.387 284.451 644.505 316.356C627.992 342.853 623.956 379.084 669.92 460.197C715.885 541.31 799.702 556.992 980.855 577M650.994 396.388C650.994 397.65 654.779 400.606 669.92 402.336C685.061 404.067 883.519 416.756 980.855 422.885V376.921L644.505 369.35C644.324 376.38 645.37 391.629 650.994 396.388Z"
                    stroke="black"
                    class="hero-main__svg-elem-8"
                  />
                </g>

                <defs>
                  <linearGradient
                    id="paint0_linear_5915_122071"
                    x1="155.66"
                    y1="251.465"
                    x2="68.6673"
                    y2="432.448"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stop-opacity="0" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_5915_122071"
                    x1="348.71"
                    y1="103.298"
                    x2="375.748"
                    y2="25.9702"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stop-opacity="0" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_5915_122071"
                    x1="261.306"
                    y1="348.029"
                    x2="496.316"
                    y2="477.095"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stop-opacity="0" />
                  </linearGradient>
                  <clipPath id="clip0_5915_122071">
                    <rect
                      width="938"
                      height="697"
                      fill="white"
                      class="hero-main__svg-elem-9"
                    />
                  </clipPath>
                </defs>
              </svg>
              <NuxtImg
                class="hero-main__image"
                :class="{ 'hero-main__image--active': animationStep >= 2 }"
                :src="data?.image?.src"
                :alt="data?.image?.alt"
                :height="data?.image?.height"
                :width="data?.image?.width"
                fetchpriority="high"
                loading="eager"
              />
              <NuxtImg
                class="hero-main__headlights"
                :class="{
                  'hero-main__headlights--active': animationStep === 7,
                }"
                :src="data?.image_active?.src"
                :alt="data?.image_active?.alt"
                :height="data?.image_active?.height"
                :width="data?.image_active?.width"
                fetchpriority="high"
                loading="eager"
              />
            </div>
          </div>
          <div class="hero-main__text">
            <h1
              v-show="data?.title"
              class="mb-0 hero-main__title h3"
              :class="{ 'hero-main__title--active': animationStep >= 3 }"
              v-html="data?.title"
            ></h1>
            <Buttons
              v-if="data?.buttons && data?.buttons.length > 0"
              class="hero-main__buttons"
              :class="{ 'hero-main__buttons--active': animationStep >= 5 }"
              :template="'main'"
              :buttons="data?.buttons"
              :background-color="data?.background_color"
              :buttons-prefix-class="'js-button-hero-main'"
            />
          </div>
        </div>
        <div class="hero-main__bottom">
          <ul
            v-if="data?.services && data?.services.length > 0"
            class="m-0 p-0 hero-main__services"
          >
            <template
              v-for="(servicesItem, servicesItemIndex) in data.services"
              :key="servicesItemIndex"
            >
              <li
                v-if="servicesItem?.title || servicesItem?.url"
                class="m-0 hero-main__services-item"
                :class="{
                  'hero-main__services-item--active': animationStep >= 4 || animationStep >= 5
                }"
              >
                <template v-if="servicesItem?.url && servicesItem?.url.length > 0">
                  <div class="hero-main__services-item-wrap">
                    
                    <CTagDetect
                      :to="servicesItem?.url"
                      class="hero-main__services-item-top"
                      :class="[
                        servicesItem?.description && servicesItem?.description.length > 0
                          ? 'hero-main__services-item-top--with-description'
                          : '',
                      ]"
                    >
                      <NuxtImg
                        v-if="servicesItem?.image?.src"
                        :src="servicesItem?.image?.src"
                        :alt="servicesItem?.image?.alt"
                        :height="servicesItem?.image?.height"
                        :width="servicesItem?.image?.width"
                        class="hero-main__services-item-image"
                        preload
                        fetchpriority="high"
                        loading="eager"
                      />
                      <div
                        v-if="servicesItem?.title && servicesItem?.title.length > 0"
                        class="hero-main__services-item-title hero-main__services-item-title--with-arrow"
                      >
                        <p class="mb-0 h6" v-html="servicesItem?.title"></p>
                        <CIcon
                          name="arrow-red"
                          width="16"
                          height="16"
                          class="hero-main__services-item-icon"
                        />
                      </div>
                    </CTagDetect>

                    <div
                      v-if="servicesItem?.description && servicesItem?.description.length > 0"
                      class="hero-main__services-item-description label"
                      v-html="servicesItem?.description"
                    ></div>
                  </div>
                </template>

                <template v-else>
                  <div class="hero-main__services-item-wrap">
                    <div
                      class="hero-main__services-item-top"
                      :class="[
                        servicesItem?.description
                          ? 'hero-main__services-item-top--with-description'
                          : '',
                      ]"
                    >
                      <NuxtImg
                        v-if="servicesItem?.image?.src"
                        :src="servicesItem?.image?.src"
                        :alt="servicesItem?.image?.alt"
                        :height="servicesItem?.image?.height"
                        :width="servicesItem?.image?.width"
                        class="hero-main__services-item-image"
                        preload
                        fetchpriority="high"
                        loading="eager"
                      />
                      <div
                        v-if="servicesItem?.title && servicesItem?.title.length > 0"
                        class="hero-main__services-item-title"
                      >
                        <p class="mb-0 h6" v-html="servicesItem?.title"></p>
                      </div>
                    </div>
                    <div
                      v-if="servicesItem?.description && servicesItem?.description.length > 0"
                      class="hero-main__services-item-description label"
                      v-html="servicesItem?.description"
                    ></div>
                  </div>
                </template>
              </li>
            </template>
          </ul>
          <ApplicationsList
            v-if="data?.applications && data?.applications.length > 0"
            :data="data?.applications"
            :view="'dark'"
            class="hero-main__applications"
            :class="{ 'hero-main__applications--active': animationStep >= 6 }"
            :buttons-prefix-class="'js-button-hero-main-application'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.hero-main {
  $parent: &;
  &__media {
    aspect-ratio: 938/697;
    height: auto;
    position: absolute;
    right: -23%;
    top: 39.5%;
    width: 120%;
    z-index: -1;
    transform: translateX(0);
    transition: transform 1226ms cubic-bezier(0.4, 0, 0.2, 1);
    &--active {
      transition-delay: 2365ms;
      transform: translateX(5%);
    }
    @include media-breakpoint-up(sm) {
      right: -36%;
      top: 5.5%;
      width: 129%;
    }
    @include media-breakpoint-up(md) {
      right: -16.5%;
      top: -171px;
      width: 79.5%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(top, -171);
    }
    &-wrap {
      height: 100%;
      position: relative;
      width: 100%;
      &:before {
        background: linear-gradient(
          0deg,
          rgba(239, 239, 239, 0) 0%,
          #efefef 59.67%
        );
        content: '';
        height: 33%;
        position: absolute;
        right: -50vw;
        top: 67%;
        transform: rotate(180deg);
        width: 200vw;
        z-index: 3;
        @include media-breakpoint-up(sm) {
          background: linear-gradient(
            0deg,
            rgba(239, 239, 239, 0) 0%,
            #efefef 46.23%
          );
          height: 42%;
          top: 58%;
        }
        @include media-breakpoint-up(md) {
          height: 25%;
          top: 75%;
        }
      }
    }
  }
  &__svg {
    left: 0;
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 1;
    &--active {
      #{$parent} {
        &__svg {
          &-elem-1 {
            stroke-dashoffset: 0;
          }
          &-elem-2 {
            stroke-dashoffset: 0;
          }
          &-elem-3 {
            stroke-dashoffset: 0;
          }
          &-elem-4 {
            stroke-dashoffset: 0;
          }
          &-elem-5 {
            stroke-dashoffset: 0;
          }
          &-elem-6 {
            stroke-dashoffset: 0;
          }
          &-elem-7 {
            stroke-dashoffset: 0;
          }
          &-elem-8 {
            stroke-dashoffset: 0;
          }
          &-elem-9 {
            stroke-dashoffset: 0;
            fill: rgb(255, 255, 255);
          }
        }
      }
    }
    &-elem-1 {
      stroke-dashoffset: 886.6013793945312px;
      stroke-dasharray: 886.6013793945312px;
      -webkit-transition:
        stroke-dashoffset 3s ease 0s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 0s;
      transition:
        stroke-dashoffset 3s ease 0s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 0s;
    }
    &-elem-2 {
      stroke-dashoffset: 311.44436310447173px;
      stroke-dasharray: 311.44436310447173px;
      -webkit-transition:
        stroke-dashoffset 3s ease 0.2s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 0.5s;
      transition:
        stroke-dashoffset 3s ease 0.2s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 0.5s;
    }
    &-elem-3 {
      stroke-dashoffset: 236.6958207790791px;
      stroke-dasharray: 236.6958207790791px;
      -webkit-transition:
        stroke-dashoffset 3s ease 0.4s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 1s;
      transition:
        stroke-dashoffset 3s ease 0.4s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 1s;
    }
    &-elem-4 {
      stroke-dashoffset: 474.53260589321724px;
      stroke-dasharray: 474.53260589321724px;
      -webkit-transition:
        stroke-dashoffset 3s ease 0.6000000000000001s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 1.5s;
      transition:
        stroke-dashoffset 3s ease 0.6000000000000001s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 1.5s;
    }
    &-elem-5 {
      stroke-dashoffset: 365.8071107007202px;
      stroke-dasharray: 365.8071107007202px;
      -webkit-transition:
        stroke-dashoffset 3s ease 0.8s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 2s;
      transition:
        stroke-dashoffset 3s ease 0.8s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 2s;
    }
    &-elem-6 {
      stroke-dashoffset: 2369.37646484375px;
      stroke-dasharray: 2369.37646484375px;
      -webkit-transition:
        stroke-dashoffset 3s ease 1s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 2.5s;
      transition:
        stroke-dashoffset 3s ease 1s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 2.5s;
    }
    &-elem-7 {
      stroke-dashoffset: 482.00079345703125px;
      stroke-dasharray: 482.00079345703125px;
      -webkit-transition:
        stroke-dashoffset 3s ease 1.2000000000000002s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 3s;
      transition:
        stroke-dashoffset 3s ease 1.2000000000000002s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 3s;
    }
    &-elem-8 {
      stroke-dashoffset: 1585.814208984375px;
      stroke-dasharray: 1585.814208984375px;
      -webkit-transition:
        stroke-dashoffset 3s ease 1.4000000000000001s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 3.5s;
      transition:
        stroke-dashoffset 3s ease 1.4000000000000001s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 3.5s;
    }
    &-elem-9 {
      stroke-dashoffset: 3272px;
      stroke-dasharray: 3272px;
      fill: transparent;
      -webkit-transition:
        stroke-dashoffset 3s ease 1.6s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 4s;
      transition:
        stroke-dashoffset 3s ease 1.6s,
        fill 3s cubic-bezier(0.47, 0, 0.745, 0.715) 4s;
    }
  }
  &__image {
    height: 100%;
    opacity: 0;
    position: absolute;
    width: 100%;
    transition: opacity 663ms linear;
    z-index: 2;
    object-fit: contain;
    object-position: right center;
    &--active {
      opacity: 1;
    }
  }
  &__headlights {
    height: 100%;
    opacity: 0;
    position: absolute;
    width: 100%;
    z-index: 3;
    transition: opacity 100ms linear;
    object-fit: contain;
    object-position: right center;
    &--active {
      opacity: 1;
    }
  }
  &__top {
    position: relative;
    padding-bottom: 29.5%;
    &:before {
      background: linear-gradient(
        0deg,
        rgba(239, 239, 239, 0) 0%,
        #efefef 24.4%
      );
      content: '';
      height: calc(94% + 72px);
      position: absolute;
      right: -50vw;
      top: -72px;
      width: 200vw;
    }
    @include media-breakpoint-up(sm) {
      padding-bottom: 20%;
      &:before {
        background: linear-gradient(
          0deg,
          rgba(239, 239, 239, 0) 0%,
          #efefef 46.23%
        );
        height: calc(102% + 120px);
        top: -120px;
      }
    }
    @include media-breakpoint-up(md) {
      padding-bottom: 3.6%;
      &:before {
        height: calc(37% + 168px);
        top: -168px;
      }
    }
    @include media-breakpoint-up(xl) {
      &:before {
        @include responsive-spacing(top, -168);
        height: calc(37% + px-to-vw(168));
      }
    }
  }
  &__bottom {
    padding-top: 29.5%;
    @include media-breakpoint-up(sm) {
      display: grid;
      grid-template-columns: 1fr;
      padding-top: 20%;
    }
    @include media-breakpoint-up(md) {
      padding-top: 3.6%;
      grid-template-columns: repeat(4, 3fr);
      grid-template-areas: 'applications services services services';
    }
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      order: 1;
      gap: 40px;
    }
    @include media-breakpoint-up(md) {
      max-width: 50%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__title {
    z-index: 1;
    opacity: 0;
    transform: translateY(20px);
    transition:
      opacity 0.7s ease,
      transform 0.7s ease;
    &--active {
      opacity: 1;
      transform: translateY(0);
    }
  }
  &__buttons {
    align-items: flex-start;
    z-index: 1;
    opacity: 0;
    transform: translateY(20px);
    transition:
      opacity 0.7s ease,
      transform 0.7s ease;
    &--active {
      opacity: 1;
      transform: translateY(0);
    }
  }
  &__services {
    display: grid;
    gap: 8px;
    list-style: none;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      align-items: end;
      gap: 12px;
      grid-template-columns: repeat(2, 1fr);
      order: 2;
      margin-top: 50px;
    }
    @include media-breakpoint-up(md) {
      padding: 0 0 10px 40px;
      grid-area: services;
      margin-top: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      @include responsive-spacing(padding-left, 40);
      @include responsive-spacing(padding-bottom, 10);
    }
    &-item {
      $parent: &;
      opacity: 0;
      transform: translateY(20px);
      transition:
        opacity 0.7s ease,
        transform 0.7s ease;
      &--active {
        opacity: 1;
        transform: translateY(0);
      }
      &-wrap {
        display: flex;
        flex-direction: column;
        position: relative;
      }
      &-top {
        align-items: center;
        background-color: $white;
        backdrop-filter: blur(20px);
        border-radius: 4px;
        display: grid;
        gap: 12px;
        grid-template-columns: 98px auto;
        min-height: 80px;
        padding: 4px 16px;
        width: 100%;
        @include media-breakpoint-up(sm) {
          min-height: 120px;
          padding: 16px;
          grid-template-columns: 120px auto;
        }
        @include media-breakpoint-up(md) {
          grid-template-columns: 168px auto;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 4);
          @include responsive-spacing(gap, 12);
          @include responsive-spacing(min-height, 120);
          @include responsive-spacing(padding, 16);
          grid-template-columns: px-to-vw(168) auto;
        }
        &--with-description {
          @include media-breakpoint-up(md) {
            bottom: 0;
            position: absolute;
            transition: bottom 0.3s linear;
            padding: 16px;
            border-radius: 4px 4px 0 0;
          }
          @include media-breakpoint-up(xl) {
            border-top-left-radius: px-to-vw(4);
            border-top-right-radius: px-to-vw(4);
            padding: px-to-vw(16);
          }
        }
      }
      &-image {
        height: auto;
        width: 100%;
      }
      &-title {
        &--with-arrow {
          align-items: center;
          display: grid;
          gap: 24px;
          grid-template-columns: auto 32px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 24);
            grid-template-columns: auto px-to-vw(32);
          }
        }
      }
      &-icon {
        height: auto;
        margin: 0 auto;
        width: 50%;
        @include media-breakpoint-up(md) {
          &:deep(path) {
            stroke: $black;
          }
        }
      }
      &-description {
        display: none;
        @include media-breakpoint-up(md) {
          background-color: $white;
          border-radius: 0 0 4px 4px;
          border-top: 2px solid $divider;
          color: $on-surface-variant-2;
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 32px 24px 24px;
          opacity: 0;
          transition:
            opacity 0.3s linear,
            visibility 0.3s linear;
          visibility: hidden;
        }
        &:deep(p) {
          margin-bottom: 0;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 4);
          border-bottom-left-radius: px-to-vw(4);
          border-bottom-right-radius: px-to-vw(4);
          border-top-width: px-to-vw(2);
          padding: px-to-vw(32) px-to-vw(24) px-to-vw(24);
        }
      }
      &:hover {
        #{$parent} {
          @include media-breakpoint-up(md) {
            &-top {
              border-radius: 4px 4px 0 0;
              &--with-description {
                bottom: 100%;
              }
            }
            &-icon {
              &:deep(path) {
                stroke: $primary;
              }
            }
            &-description {
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }
    }
  }
  &__applications {
    margin: 32px 0 6px;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.7s ease;
    &--active {
      opacity: 1;
    }
    @include media-breakpoint-up(sm) {
      margin-top: 0;
      order: 1;
    }
    @include media-breakpoint-up(md) {
      grid-area: applications;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-bottom, 6);
    }
  }
}
</style>
